import { commitMutation, fetchQuery, graphql } from 'react-relay';
import RelayEnvironment from 'src/relay/environment';
import { AIStudioNoCreditsError } from 'src/components/pages/aiStudio/tasks';

/**
 * Frontend client for the async AI "Side Generator" (Zakeke.AIService). Mirrors the AI Studio task
 * pattern (src/components/pages/aiStudio/tasks.ts): start a task, then poll its status until a terminal
 * state, and return the hosted result image URL. Single and bulk variants.
 *
 * - Provide `sideImageUrl` to run image-to-image (the side image is used as a reference); omit it for
 *   text-to-image. The result `resultUrl` is an already-hosted URL (no upload needed).
 */

const POLLING_INTERVAL = 2000; // 2 seconds
const MAX_POLLING_TIME = 600000; // 10 minutes

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const startMutation = graphql`
    mutation sideGeneratorStartMutation($input: SideGeneratorInput!) {
        startSideGenerator(input: $input) {
            taskId
            state
        }
    }
`;

const statusQuery = graphql`
    query sideGeneratorStatusQuery($taskId: Long!) {
        sideGeneratorTaskStatus(taskId: $taskId) {
            state
            resultUrl
            errorMessage
        }
    }
`;

const bulkStartMutation = graphql`
    mutation sideGeneratorBulkStartMutation($input: SideGeneratorBulkInput!) {
        startSideGeneratorBulk(input: $input) {
            items {
                reference
                taskId
                state
            }
        }
    }
`;

const bulkStatusQuery = graphql`
    query sideGeneratorBulkStatusQuery($taskIds: [Long!]!) {
        sideGeneratorBulkStatus(taskIds: $taskIds) {
            total
            waiting
            processing
            completed
            failed
            items {
                taskId
                state
                resultUrl
                errorMessage
            }
        }
    }
`;

export interface SideGenerateOptions {
    prompt: string;
    /** Reference image for image-to-image; omit for text-to-image. */
    sideImageUrl?: string | null;
    /** Defaults to 'auto' for image-to-image (keep reference proportions), 'square' for text-to-image. */
    aspectRatio?: string;
    model?: string;
}

const startSingle = (input: SideGenerateOptions): Promise<{ taskId: number; state: string }> =>
    new Promise((resolve, reject) => {
        commitMutation(RelayEnvironment, {
            mutation: startMutation,
            variables: { input },
            onCompleted: (response: any) => {
                const task = response?.startSideGenerator;
                if (!task) reject(new Error('Failed to start side generation'));
                else resolve({ taskId: task.taskId, state: task.state });
            },
            onError: reject
        });
    });

const pollSingle = async (taskId: number): Promise<{ state: string; resultUrl: string | null; errorMessage: string | null }> => {
    const start = Date.now();
    while (Date.now() - start < MAX_POLLING_TIME) {
        const data: any = await fetchQuery(RelayEnvironment, statusQuery, { taskId }).toPromise();
        const status = data?.sideGeneratorTaskStatus;
        if (!status) throw new Error('Task not found');
        if (status.state === 'COMPLETED' || status.state === 'ERROR' || status.state === 'NO_CREDITS') return status;
        await sleep(POLLING_INTERVAL);
    }
    throw new Error('Task timeout');
};

/**
 * Generates a single side image and resolves to the hosted result URL.
 * Throws {@link AIStudioNoCreditsError} when the seller is out of credits; a generic Error otherwise.
 */
export const generateSideImage = async (options: SideGenerateOptions): Promise<string> => {
    const aspectRatio = options.aspectRatio ?? (options.sideImageUrl ? 'auto' : 'square');
    const task = await startSingle({ ...options, aspectRatio });
    if (task.state === 'NO_CREDITS') throw new AIStudioNoCreditsError();

    const status = await pollSingle(task.taskId);
    if (status.state === 'NO_CREDITS') throw new AIStudioNoCreditsError();
    if (status.state === 'ERROR' || !status.resultUrl) throw new Error(status.errorMessage || 'Side generation failed');

    return status.resultUrl;
};

export interface SideBulkItem {
    /** Reference image for image-to-image; omit for text-to-image. */
    sideImageUrl?: string | null;
    /** Correlation key echoed back in the result (e.g. `${modelId}|${colorId}|${sideId}`). */
    reference: string;
}

export interface SideBulkOptions {
    prompt: string;
    aspectRatio?: string;
    model?: string;
    sides: SideBulkItem[];
}

export interface SideBulkResult {
    resultUrl: string | null;
    error: string | null;
}

const startBulk = (input: SideBulkOptions): Promise<{ reference: string | null; taskId: number; state: string }[]> =>
    new Promise((resolve, reject) => {
        commitMutation(RelayEnvironment, {
            mutation: bulkStartMutation,
            variables: { input },
            onCompleted: (response: any) => {
                const items = response?.startSideGeneratorBulk?.items;
                if (!items) reject(new Error('Failed to start bulk side generation'));
                else resolve(items);
            },
            onError: reject
        });
    });

/**
 * Generates side images in bulk (one task per side) and resolves to a map of reference -> result.
 * `onProgress` receives the aggregate counts while polling, for a real progress indicator.
 */
export const generateSideImagesBulk = async (
    options: SideBulkOptions,
    onProgress?: (status: { total: number; completed: number; failed: number }) => void
): Promise<Map<string, SideBulkResult>> => {
    const items = await startBulk(options);
    const taskToReference = new Map<number, string>();
    items.forEach((item) => {
        if (item.reference != null) taskToReference.set(item.taskId, item.reference);
    });
    const taskIds = items.map((item) => item.taskId);

    const start = Date.now();
    while (Date.now() - start < MAX_POLLING_TIME) {
        const data: any = await fetchQuery(RelayEnvironment, bulkStatusQuery, { taskIds }).toPromise();
        const status = data?.sideGeneratorBulkStatus;
        if (status) {
            onProgress?.({ total: status.total, completed: status.completed, failed: status.failed });
            const pending = (status.waiting ?? 0) + (status.processing ?? 0);
            if (pending === 0) {
                const result = new Map<string, SideBulkResult>();
                (status.items ?? []).forEach((item: any) => {
                    const reference = taskToReference.get(item.taskId);
                    if (reference != null)
                        result.set(reference, {
                            resultUrl: item.resultUrl ?? null,
                            error: item.state === 'COMPLETED' ? null : item.errorMessage || item.state
                        });
                });
                return result;
            }
        }
        await sleep(POLLING_INTERVAL);
    }
    throw new Error('Bulk side generation timeout');
};
